import React, { lazy, Suspense, useState, useEffect } from 'react';
import { Routes, Route, Outlet, Navigate, useLocation } from 'react-router-dom';
import { Authenticator, ThemeProvider } from '@aws-amplify/ui-react';
import { signIn, signUp, fetchAuthSession } from 'aws-amplify/auth';

// COMPONENTS
import AppToolbar from './components/AppToolbar';
import BottomNavigator from './components/BottomNavigator';

import amplifyExports from './aws-exports';
import { AuthComponents, LoginFormFields } from './components/AuthenticatorComponents';

import { UserContextProvider } from './contexts/UserContext.js';

// Dynamically import pages
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Account = lazy(() => import('./pages/Account'));
const Scan = lazy(() => import('./pages/Scan'));
const AccessToken = lazy(() => import('./pages/AccessToken'));
const ConfirmAccess = lazy(() => import('./pages/ConfirmAccess'));
const Trigger = lazy(() => import('./pages/Trigger'));
const Invite = lazy(() => import('./pages/Invite'));
const ManagerDashboard = lazy(() => import('./manager/ManagerDashboard'));
const ProvisionDownload = lazy(() => import('./manager/pages/ProvisionDownload'));

// FUNCTIONS
import { padding } from './functions/styleHelpers';
import qrCodeBackground from './assets/qrCode.png';

// THEME
import amplifyTheme from './styles/amplifyTheme';

const deviceActionUrls = [
  { code: 'ta', url: '/ta/:id', action: 'AuthToggle' },
  { code: 'th', url: '/th/:id', action: 'Check in' },
  { code: 'to', url: '/to/:id', action: 'On' },
  { code: 'tb', url: '/tb/:id', action: 'Check out' },
  { code: 'tt', url: '/tt/:id', action: 'Toggle' },
  { code: 'tp', url: '/tp/:id', action: 'Pulse' },
  { code: 'tm', url: '/tm/:id', action: 'Multi Channel' },
  { code: 'ks', url: '/ks/:id', action: 'Key Store' },
];

const RequireAuth = ({ children }) => {
  const location = useLocation();
  const [authStatus, setAuthStatus] = useState('loading'); // 'loading' | 'authenticated' | 'unauthenticated'

  const containerStyle = {
    backgroundImage: `linear-gradient(rgba(255,255,255,0.1), rgba(255,255,255,0.1)), url(${qrCodeBackground})`,
    backgroundRepeat: 'repeat',
    backgroundColor: 'transparent',
    height: '100vh',
  };

  const services = import.meta.env.VITE_APP_REGION === "local" ? {
    async handleSignIn(input) {
      // Use USER_PASSWORD_AUTH — cognito-local does not support USER_SRP_AUTH
      const { username, password } = input;
      return signIn({
        username,
        password,
        options: {
          authFlowType: 'USER_PASSWORD_AUTH',
        },
      });
    },
    async handleSignUp(input) {
      // Disable auto-sign-in after confirmation.
      // cognito-local does not support the SRP auth flow that autoSignIn uses,
      // so after confirmation the Authenticator returns to the sign-in form
      // where handleSignIn (above) uses USER_PASSWORD_AUTH instead.
      const { username, password, options } = input;
      return signUp({
        username,
        password,
        options: {
          ...options,
          autoSignIn: false,
        },
      });
    },
  } : {
    async handleSignIn(input) {
      // Handle both password and social sign-in
      if (input.provider) {
        return signIn({ 
          provider: input.provider 
        });
      }

      const { username, password } = input;
      return signIn({
        username,
        password,
      });
    },
  };

  const isDeviceActionUrl = deviceActionUrls.find((el) => location.pathname.split('/')[1] === el.code);

  useEffect(() => {
    if (!isDeviceActionUrl) return;
    fetchAuthSession()
      .then(session => setAuthStatus(session?.tokens ? 'authenticated' : 'unauthenticated'))
      .catch(() => setAuthStatus('unauthenticated'));
  }, [location.pathname]);

  if (isDeviceActionUrl) {
    // Still resolving auth — render nothing to avoid a flash of the wrong UI
    if (authStatus === 'loading') return null;

    if (authStatus === 'authenticated') {
      // Cases 1 & 4: authenticated user (with or without access keys) — use the normal app shell
      return (
        <div style={containerStyle}>
          <ThemeProvider theme={amplifyTheme}>
            <UserContextProvider>
              <AppToolbar />
              <div style={padding(7, 7, 70, 7)}>
                <Suspense fallback={<div>Loading...</div>}>
                  <Outlet />
                </Suspense>
              </div>
              <BottomNavigator />
            </UserContextProvider>
          </ThemeProvider>
        </div>
      );
    }

    // Unauthenticated — check whether a local access key covers this device
    const deviceId = location.pathname.split('/')[2];
    const AccessKeys = JSON.parse(localStorage.getItem('AccessKeys') || null) || [];
    const validAccessKeys = AccessKeys.filter((k) => new Date(k.endTime) > new Date());
    if (AccessKeys.length !== validAccessKeys.length) {
      localStorage.setItem('AccessKeys', JSON.stringify(validAccessKeys));
    }
    const keys = validAccessKeys.filter((k) => k.devices.find((d) => d.uuid === deviceId));

    if (keys.length > 0) {
      // Case 3: unauthenticated with a valid access key → hand off to the AccessToken flow
      return <Navigate to={`/key/${encodeURIComponent(keys[0].key)}`} state={{ ...keys[0], uuid: deviceId, local: true }} />;
    }

    // Case 2: unauthenticated, no access key — fall through to the Authenticator (login form)
  }

  return (
    <div style={containerStyle}>
      <ThemeProvider theme={amplifyTheme}>
        <Authenticator
          services={services}
          components={AuthComponents()}
          formFields={LoginFormFields}
          loginMechanisms={['email']}
          signUpAttributes={['phone_number', 'email']}
          socialProviders={amplifyExports.Auth.Cognito.aws_cognito_social_providers} //['google', 'facebook']
        >
          {({ signOut, user }) => (
            <UserContextProvider user={user}>
              <AppToolbar />
              <div style={padding(7, 7, 70, 7)}>
                <Suspense fallback={<div>Loading...</div>}>
                  <Outlet />
                </Suspense>
              </div>
              <BottomNavigator />
            </UserContextProvider>
          )}
        </Authenticator>
      </ThemeProvider>
    </div>
  );
}



const MyRoutes = () => {
  // const location = useLocation();
  // const { route } = useAuthenticator((context) => [context.route]);
  return (
    <Routes>
      <Route element={<RequireAuth />}>
        <Route index element={<Dashboard />} />
        <Route path='/dashboard' element={<Navigate to='/' />} />
        <Route path='/scan' element={<Scan initialScan={true} />} />
        <Route path='/account' element={<Account />} />
        <Route path='/trigger/:id' element={<Trigger method={'Dashboard Button'} />} />
        <Route path='/im/:id' element={<Invite />} />
        <Route path='/is/:id' element={<Invite />} />
        <Route path='/invite' element={<Invite />} />
        {deviceActionUrls.map((el) => (
          <Route key={el.url} path={el.url} element={<Trigger method={'Scanned QR'} />} />
        ))}
        <Route path='/manage' element={<ManagerDashboard />} />
        <Route path='/manage/:activeEntityId/:activeTab' element={<ManagerDashboard />} />
        <Route path='/provision-download' element={<ProvisionDownload />} />
      </Route>
      <Route
        path='/key/:encodedData'
        element={
          <AccessToken />
        }
      />
      <Route
        path='/confirm-access/:inviteToken'
        element={
          <Suspense fallback={<div>Loading...</div>}>
            <ConfirmAccess />
          </Suspense>
        }
      />
      {/* <Route path='/login' element={<Login initialState={'signIn'} />} /> */}
      {/* {<Route path="/invite/:encodedData" element={route === 'authenticated' ? <Dashboard invite={location.pathname.split('/')[2]}/> : <Login initialState={"signUp"} invite={location.pathname.split('/')[2]}/>} />} */}
      {/* <Route path="/invite/:encodedData" element={<Login initialState={"signUp"} invite={location.pathname.split('/')[2]} />} /> */}


      <Route path='*' element={<Navigate to='/' />} />
    </Routes>
  );
};

export default MyRoutes;

